import React, { useState } from 'react';
import { Wrapper } from '@googlemaps/react-wrapper';
import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from '@reach/combobox';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useEffectOnce } from 'react-use';
import styled from 'styled-components';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';
import {
  FormattedMessage,
  useFormatMessage,
} from '@artemis/integrations/contentful/utils';
import { getIsCreateTeamLoading } from '@artemis/store/teams/selectors';
import { logCreateCompanyImpression } from './analytics';
import {
  ButtonContainer,
  Container,
  Description,
  PrimaryButton,
  Title,
} from './shared';

const Input = styled.input`
  padding: 10px 20px;
  width: 100%;
  height: 44px;
  background: ${({ theme }) => theme.rtColors.black100};
  border-style: none;
  border-radius: 6px;
  box-sizing: border-box;
  display: block;
`;

const AddressInput = styled(ComboboxInput)`
  padding: 10px 20px;
  width: 100%;
  height: 44px;
  background: ${({ theme }) => theme.rtColors.black100};
  border-style: none;
  border-radius: 6px;
  box-sizing: border-box;
  display: block;
`;

const NoResults = styled.span`
  display: block;
  margin: 8px;
`;

const Popover = styled(ComboboxPopover)`
  z-index: ${props => props.theme.zIndices.popover};
`;

export const CreateTeam = ({ email, onNext }) => {
  const [name, setName] = useState('');
  const [googlePlaceId, setGooglePlaceId] = useState();
  const isLoading = useSelector(getIsCreateTeamLoading);

  const namePlaceholder = useFormatMessage({
    entry: 'teams.create.namePlaceholder',
  });
  const inputPlaceholder = useFormatMessage({
    entry: 'teams.create.addressPlaceholder',
  });

  useEffectOnce(() => {
    logCreateCompanyImpression({ email });
  });

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
    requestOptions: {
      types: ['address'],
    },
  });

  const handleInput = e => setValue(e.target.value);

  const handleSelect = ({ description }) => {
    setValue(description, false);
    clearSuggestions();
    getGeocode({ address: description })
      .then(results => {
        const [result] = results;
        setGooglePlaceId(result.place_id);
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error: ', error);
      });
  };

  const filtered = data?.filter(({ types }) => !types.includes('route')) || [];

  return (
    <Container>
      <Title>
        <FormattedMessage entry="teams.create.title" />
      </Title>
      <Description>
        <FormattedMessage entry="teams.create.description" />
      </Description>
      <Input
        aria-label={namePlaceholder}
        placeholder={namePlaceholder}
        autoFocus
        value={name}
        onChange={e => setName(e.target.value)}
      />
      <Combobox
        aria-label={inputPlaceholder}
        onSelect={val => {
          const entry = data.find(curr => curr.description === val);
          handleSelect(entry);
        }}
      >
        <AddressInput
          type="text"
          value={value}
          onChange={handleInput}
          disabled={!ready}
          placeholder={inputPlaceholder}
          autoFocus
          autoComplete="off"
        />
        {status === 'OK' && (
          <Popover>
            {filtered.length > 0 ? (
              <ComboboxList>
                {filtered.map(({ place_id: id, description }) => (
                  <ComboboxOption
                    key={id}
                    value={description}
                    data-testid={`location-option-${id}`}
                  />
                ))}
              </ComboboxList>
            ) : (
              <NoResults>
                <FormattedMessage entry="teams.create.noAddressesFound" />
              </NoResults>
            )}
          </Popover>
        )}
      </Combobox>
      <ButtonContainer>
        <PrimaryButton
          onClick={() => onNext({ name, googlePlaceId })}
          isLoading={isLoading}
          isDisabled={!name || !googlePlaceId}
        >
          <FormattedMessage entry="global.next" />
        </PrimaryButton>
      </ButtonContainer>
    </Container>
  );
};

CreateTeam.propTypes = {
  email: PropTypes.string.isRequired,
  onNext: PropTypes.func.isRequired,
};

const WrappedCreateTeam = props => (
  <Wrapper apiKey={process.env.RT_GOOGLE_MAPS_API_KEY} libraries={['places']}>
    <CreateTeam {...props} />
  </Wrapper>
);

export default WrappedCreateTeam;
